import { Component, inject, OnInit, ViewChild, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import {
  ButtonComponent, SearchBarComponent, CardComponent,
  SpinnerComponent, EmptyStateComponent, BadgeComponent,
  ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';

import { CreateIslandModalComponent } from './create-island-modal.component';
import { IslandService, type Island } from './island.service';

@Component({
  selector: 'es-islands-list',
  imports: [
    ButtonComponent, SearchBarComponent, CardComponent, SpinnerComponent,
    EmptyStateComponent, BadgeComponent, CreateIslandModalComponent,
  ],
  template: `
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold font-exo">Islands</h1>
          <p class="text-sm text-[var(--text-secondary)]">{{ filteredIslands().length }} islands</p>
        </div>
        <es-button variant="primary" (clicked)="createIsland()">+ New Island</es-button>
      </div>

      <!-- Search -->
      <es-search-bar
        class="mb-4 block"
        placeholder="Search islands..."
        [(value)]="searchQuery"
      ></es-search-bar>

      <!-- Loading -->
      @if (islandService.loading()) {
        <div class="flex justify-center py-12">
          <es-spinner label="Loading islands..."></es-spinner>
        </div>
      } @else if (filteredIslands().length === 0 && !searchQuery()) {
        <es-empty-state
          title="No islands yet"
          description="Create your first island to get started."
          icon="🏝"
          actionLabel="Create Island"
          (action)="createIsland()"
        ></es-empty-state>
      } @else {
        <!-- Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          @for (island of filteredIslands(); track island.id) {
            <es-card [clickable]="true" (clicked)="openIsland(island)" padding="none">
              <!-- Thumbnail -->
              <div class="h-32 gradient-blue-violet rounded-t-lg flex items-center justify-center">
                <span class="text-4xl opacity-50">🏝</span>
              </div>
              <div class="p-4">
                <div class="flex items-center justify-between mb-1">
                  <h3 class="text-base font-semibold truncate">{{ island.name }}</h3>
                  <es-badge [label]="island.slug" variant="secondary"></es-badge>
                </div>
                @if (island.description) {
                  <p class="text-xs text-[var(--text-secondary)] line-clamp-2">{{ island.description }}</p>
                }
              </div>
            </es-card>
          }
        </div>
      }

      <es-create-island-modal
        #createModal
        [open]="createOpen()"
        (closed)="createOpen.set(false)"
        (submitted)="onCreateSubmit($event)"
      ></es-create-island-modal>
    </div>
  `,
})
export class IslandListComponent implements OnInit {
  @ViewChild('createModal') createModalRef?: CreateIslandModalComponent;

  readonly islandService = inject(IslandService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  searchQuery = signal('');
  readonly createOpen = signal(false);

  filteredIslands = computed(() => {
    const q = this.searchQuery().toLowerCase();
    if (!q) return this.islandService.islands();
    return this.islandService.islands().filter(i =>
      i.name.toLowerCase().includes(q) || i.slug.toLowerCase().includes(q),
    );
  });

  ngOnInit() {
    this.islandService.loadIslands();
  }

  openIsland(island: Island) {
    this.router.navigate(['/islands', island.id]);
  }

  createIsland() {
    this.createOpen.set(true);
  }

  async onCreateSubmit(payload: { name: string; slug: string; description: string }) {
    try {
      const island = await this.islandService.createIsland(payload);
      this.createModalRef?.finishSubmit();
      this.toast.success(`Island "${island.name}" created`);
      this.createOpen.set(false);
      this.router.navigate(['/islands', island.id]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create island';
      this.createModalRef?.finishSubmit(message);
    }
  }
}
